<template>
    <v-dialog v-model="dialog" fullscreen persistent>
        <v-card flat v-if="!loading">
            <v-card-title class="primary white--text headline">
                {{'Point Selector'|translate}}
                <v-spacer></v-spacer>
            </v-card-title>
            <v-card-text>
                <v-layout row wrap>
                    <v-flex xs12>
                        <div style="width:100%; background-color: rgb(240,240,240)">
                            <v-btn round color="primary" dark v-for="(value,facet) in activeFacets" v-bind:key="facet">
                                {{facets[facet]}}
                                <v-icon small class="ml-2 mr-2" v-if="value.startsWith('NOT ')"
                                        @click="invertFacet(facet)">fa-not-equal
                                </v-icon>
                                <v-icon small class="ml-2 mr-2" v-if="!value.startsWith('NOT ')"
                                        @click="invertFacet(facet)">fa-equals
                                </v-icon>
                                <span v-if="value.startsWith('NOT ')">{{value.substr(4)}} </span>
                                <span v-if="!value.startsWith('NOT ')">{{value}} </span>
                                <v-icon class="ml-2" @click="clearFacet(facet)">fa-times-circle</v-icon>
                            </v-btn>
                        </div>
                    </v-flex>
                    <v-flex xs12 v-if="showFilters">
                        <v-layout row wrap>
                            <v-flex xs12 v-for="facetGraph in facetGraphs" v-bind:key="facetGraph.facet"
                                    v-bind:class="[facetGraph['flex-class'], 'md3']">
                                <div class="pl-3 pr-3 pt-3">
                                    <v-autocomplete
                                            solo-inverted
                                            :items="facetOptions[facetGraph.facet]"
                                            :label="facetGraph.label"
                                            @change="selectFacet($event, facetGraph.facet)"
                                    ></v-autocomplete>
                                </div>

                                <div class="pa-4">
                                    <vue-plotly disable-responsive :data="facetGraph.data" :layout="facetGraph.layout"
                                                :options="{}" @click="handleClick"></vue-plotly>

                                </div>
                            </v-flex>
                        </v-layout>
                    </v-flex>

                    <v-flex xs12>
                        <v-layout row wrap>
                            <v-flex xs6 class="pa-4">
                                <v-card-title class="accent white--text title">
                                    {{'Available points'|translate}}
                                </v-card-title>
                                <v-layout row>
                                    <span style="width:300px;"><v-text-field class="mr-4" clearable v-model="search"
                                                                             type="text" :label="'Search'|translate"></v-text-field></span>
                                    <v-btn class="accent" @click="showFilters=!showFilters">
                                        <v-icon small class="mr-2">fa-filter</v-icon>
                                        <span v-show="!showFilters">{{'Show filters'|translate}}</span>
                                        <span v-show="showFilters">{{'Hide filters'|translate}}</span>
                                    </v-btn>
                                    <v-spacer></v-spacer>
                                    <v-btn class="accent" @click="selectFromTable" v-show="tableSelected.length>0">
                                        <v-icon small class="mr-2">fa-arrow-right</v-icon>
                                    </v-btn>
                                </v-layout>


                                <v-data-table
                                        :headers="headers"
                                        disable-initial-sort
                                        v-model="tableSelected"
                                        select-all
                                        :items="nonSelectedItems"
                                        :search="search2"
                                        :rows-per-page-items="[10,25,50,100]"
                                >
                                    <template v-slot:items="props">
                                        <tr :active="props.selected" @click="props.selected = !props.selected">
                                            <td>
                                                <v-checkbox
                                                        :input-value="props.selected"
                                                        primary
                                                        hide-details
                                                ></v-checkbox>
                                            </td>
                                            <td class="text-xs-left cursor-as-pointer">{{ props.item.name }}</td>
                                            <td class="text-xs-left cursor-as-pointer">{{ props.item.floor }}</td>
                                            <td class="text-xs-left cursor-as-pointer">{{ props.item.equipment_name }}
                                            </td>
                                            <td class="text-xs-left cursor-as-pointer">{{ props.item.equipment_type }}
                                            </td>
                                        </tr>
                                    </template>
                                </v-data-table>

                            </v-flex>
                            <v-flex xs6 class="pa-4">
                                <v-card-title class="accent white--text title">
                                    {{'Selected points'|translate}}
                                </v-card-title>
                                <v-layout row>
                                    <v-spacer></v-spacer>
                                    <v-btn class="accent mb-4" @click="selectedPoints=[]">
                                        <v-icon small class="mr-2">fa-trash</v-icon>
                                        {{'Clear selection'|translate}}
                                    </v-btn>
                                </v-layout>


                                <v-data-table
                                        :headers="headers2"
                                        disable-initial-sort
                                        :items="selectedPoints"
                                        hide-actions
                                >
                                    <template v-slot:items="props">
                                        <td class="text-xs-left">{{ props.item.name }}</td>
                                        <td class="text-xs-left">{{ props.item.floor }}</td>
                                        <td class="text-xs-left">{{ props.item.equipment_name }}</td>
                                        <td class="text-xs-left">{{ props.item.equipment_type }}</td>
                                        <td class="text-xs-left">
                                            <v-icon @click="deselectPoint(props.item)" small
                                                    class="mr-2  cursor-as-pointer">fa-trash
                                            </v-icon>
                                        </td>
                                    </template>
                                </v-data-table>

                            </v-flex>
                        </v-layout>
                    </v-flex>

                </v-layout>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn class="primary" @click="saveSelection" :disabled="selectedPoints.length==0">{{'Add selected points'|translate}}
                </v-btn>
                <v-btn @click="dialog=false">{{'Cancel'|translate}}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>

    import VuePlotly from "../components/VuePlotly";

    export default {
        name: 'PointPickerDialog',
        components: {VuePlotly},
        props: {
            value: {type: Boolean, default: true},
            callback: {type: Function},
        },
        data() {
            return {
                dialog: false,
                activeFacets: {},
                facetOptions: {},
                showFilters: false,
                search: '',
                search2: '',
                timeout: null,
                items: null,
                facets: {
                    "floor": "Floor",
                    "equipment_type": "Equipment Type",
                    "equipment_name": "Equipment Name",
                    "name": "Name",
                },
                facetGraphs: [],
                loading: true,
                rawPoints: null,
                points: null,
                selectedPoints: [],
                tableSelected: []
            }
        },
        computed: {
            nonSelectedItems() {
                let ids = this.selectedPoints.map(x => x.id)
                return this.items.filter(x => ids.indexOf(x.id) == -1)

            },
            headers() {
                return [
                    {text: this.$options.filters.translate('Name'), value: 'name', sortable: true},
                    {text: this.$options.filters.translate('Floor'), value: 'floor', sortable: true},
                    {text: this.$options.filters.translate('Equipment Name'), value: 'equipment_name', sortable: true},
                    {text: this.$options.filters.translate('Equipment Type'), value: 'equipment_type', sortable: true},
                ]
            },
            headers2() {
                return [
                    {text: this.$options.filters.translate('Name'), value: 'name', sortable: true},
                    {text: this.$options.filters.translate('Floor'), value: 'floor', sortable: true},
                    {text: this.$options.filters.translate('Equipment Name'), value: 'equipment_name', sortable: true},
                    {text: this.$options.filters.translate('Equipment Type'), value: 'equipment_type', sortable: true},
                    {text: this.$options.filters.translate('Actions'), sortable: false},
                ]
            }
        },
        watch: {
            value() {
                if (this.dialog != this.value) {
                    this.dialog = this.value
                    this.reset()
                }
            },
            dialog() {
                this.$emit('input', this.dialog)
            },
            search() {
                this.debounce(this.syncSearches, 500);
            }
        },
        methods: {
            reset() {
                this.search = ''
                this.search2 = ''
                this.showFilters = false
                this.activeFacets = {}
                this.facetOptions = {}
                this.selectedPoints = []
                this.tableSelected = []
                this.getData()
            },
            saveSelection() {
                this.callback(JSON.parse(JSON.stringify(this.selectedPoints)))
                this.dialog = false
            },
            selectFromTable() {
                this.selectedPoints = this.selectedPoints.concat(JSON.parse(JSON.stringify(this.tableSelected)))
                this.tableSelected = []
            },
            selectPoint(point) {
                this.selectedPoints.push(JSON.parse(JSON.stringify(point)))
            },
            deselectPoint(point) {
                this.selectedPoints.splice(this.selectedPoints.indexOf(point), 1)
            },
            syncSearches() {
                this.search2 = this.search
            },
            debounce(callback, wait) {
                clearTimeout(this.timeout);
                this.timeout = setTimeout(() => callback(), wait);
            },
            selectFacet(event, facet) {
                this.activeFacets[facet] = event
                this.refinePoints()
            },
            handleClick(data) {
                let facet = data.points[0].fullData.name
                let value = data.points[0].y
                this.activeFacets[facet] = value
                this.refinePoints()
            },
            clearFacet(facet) {
                delete (this.activeFacets[facet])
                this.refinePoints()
            },
            invertFacet(facet) {
                if (this.activeFacets[facet].startsWith("NOT ")) {
                    this.activeFacets[facet] = this.activeFacets[facet].substr(4)
                } else {
                    this.activeFacets[facet] = "NOT " + this.activeFacets[facet]
                }
                this.refinePoints()
            },
            getData() {
                let me = this;
                me.loading = true
                let output = []
                if (!this.$store.state.pointsListStore.pointsList) return [{id: 1, name: 'loading'}]
                let id = 1
                for (let i in this.$store.state.pointsListStore.pointsList.sensors) {
                    for (let j in this.$store.state.pointsListStore.pointsList.sensors[i]) {
                        output.push({
                            id: id,
                            name: this.$store.state.pointsListStore.pointsList.sensors[i][j],
                            equipment_name: this.$store.state.pointsListStore.pointsList.equipment_details[i].equipment_name,
                            equipment_type: this.$store.state.pointsListStore.pointsList.equipment_details[i].equipment_type,
                            floor: this.$store.state.pointsListStore.pointsList.equipment_details[i].floor,
                            facility_name: this.$store.state.pointsListStore.pointsList.facility_name
                        })
                        id++

                    }
                }
                output.sort((a, b) => (a.name > b.name) ? 1 : -1)
                me.rawPoints = output
                me.refinePoints()
            },
            refinePoints() {
                let me = this
                me.loading = true
                let points = [...me.rawPoints]
                for (let activeFacet in me.activeFacets) {
                    if (activeFacet === "floor" && me.activeFacets[activeFacet].startsWith("NOT ")) {
                        points = points.filter(x => !x[activeFacet].startsWith(me.activeFacets[activeFacet].substr(4)))
                    } else if (activeFacet === "floor") {
                        points = points.filter(x => x[activeFacet].startsWith(me.activeFacets[activeFacet]))
                    } else if (me.activeFacets[activeFacet].startsWith("NOT ")) {
                        points = points.filter(x => x[activeFacet] !== me.activeFacets[activeFacet].substr(4))
                    } else {
                        points = points.filter(x => x[activeFacet] === me.activeFacets[activeFacet])
                    }
                }
                let facetGraphs = []
                let facetOptions = {}
                for (let facet in me.facets) {
                    if (me.activeFacets[facet] && facet !== "floor") {
                        continue
                    }
                    let facetObj = {}
                    for (let point of points) {
                        if (facet === "floor") {
                            if (point[facet] && point[facet] !== "") {
                                let subfacets = point[facet].split(">")
                                if (subfacets.length > 1) {

                                    let prev = [...subfacets]
                                    subfacets = subfacets.map((value, index) => {
                                        if (index === 0) {
                                            return value
                                        } else {
                                            return (prev.slice(0, index).join(">") + ">" + value)
                                        }
                                    })

                                }
                                for (let subfacet of subfacets) {
                                    if (subfacet !== me.activeFacets[facet] && subfacet !== "NOT " + me.activeFacets[facet]) {
                                        if (!facetObj[subfacet]) {
                                            facetObj[subfacet] = 1;
                                        } else {
                                            facetObj[subfacet] += 1;
                                        }
                                    }
                                }
                            }
                        } else {
                            if (!facetObj[point[facet]]) {
                                facetObj[point[facet]] = 1;
                            } else {
                                facetObj[point[facet]] += 1;
                            }
                        }
                    }
                    if (Object.keys(facetObj).length <= 1) {
                        continue
                    }

                    let sorted = Object.keys(facetObj).sort(function (a, b) {
                        return facetObj[b] - facetObj[a]
                    })
                    facetOptions[facet] = [...sorted];
                    if (facet === "floor" && !me.activeFacets[facet]) {
                        sorted = sorted.filter(x => x.indexOf('>') < 0)
                    }
                    let top10 = sorted.slice(0, 10);
                    let top10Scores = []
                    for (let top of top10) {
                        top10Scores.push(facetObj[top])
                    }
                    let facetDef = {
                        "facet": facet,
                        "label": me.facets[facet],
                        "data": [{
                            type: 'bar',
                            name: facet,
                            x: top10Scores.reverse(),
                            y: top10.reverse(),
                            orientation: 'h',
                        }],
                        "layout": {
                            title: me.facets[facet],
                            'margin': {'l': 0, 'r': 0, 't': 0, 'b': 0},
                            'xaxis': {'automargin': true},
                            'yaxis': {'automargin': true},
                        }
                    }
                    if (facet == 'title') {
                        facetDef['flex-class'] = 'md8'

                    }

                    facetGraphs.push(facetDef)
                }
                me.facetOptions = facetOptions
                me.items = points
                me.facetGraphs = facetGraphs
                me.loading = false
                me.search = ''
                me.search2 = ''
            }
        },


    }
</script>

<style scoped>

</style>
